import _ from "lodash";
import React, { useCallback, useEffect } from "react";
import { Checkbox } from "@components";
import { BudgetCategory, Category } from "@models";
import { useFormContext } from "react-hook-form";
import { escapeCategoryId } from "../util";
import { ConfigModuleSetting } from "./types";
import { Alert } from "react-bootstrap";
import { UpdatedDivision } from "@models/Division";
import { OneOf } from "@util/types";

type Props = {
	value: string[];
	onChange: (value: string[]) => void;
	onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
	name: string;
	budgetCategory: BudgetCategory;
};

export default function FlatCategorySelection(props: Props) {
	const { budgetCategory, onChange } = props;
	const categories = budgetCategory.subCategories;
	const {
		watch,
		setError,
		clearErrors,
		formState: { errors, defaultValues },
	} = useFormContext<OneOf<ConfigModuleSetting, UpdatedDivision>>();
	const selectedCategories = watch().categories;
	const originalCategories = defaultValues?.categories || [];
	const fieldName = escapeCategoryId(budgetCategory.id);
	const namePrefix = `amounts.${fieldName}` as const;
	const isCategoryActive: boolean = watch(`${namePrefix}.enabled`);

	const isChecked = useCallback(
		(category: Category) => selectedCategories.includes(category.id),
		[selectedCategories]
	);

	const shouldShowDeprecatedCategory = useCallback(
		(category: Category) => {
			if (!category.isDeprecated) return true;

			// Show if currently selected
			if (selectedCategories.includes(category.id)) return true;

			// Show if it was originally selected but now deselected (not yet saved)
			return (
				originalCategories.includes(category.id) &&
				!selectedCategories.includes(category.id)
			);
		},
		[selectedCategories, originalCategories]
	);

	// set form to invalid and show alert if amounts of category is enabled but has no categories selected.
	useEffect(() => {
		const subCategories = budgetCategory.subCategories.map((c) => c.id);

		if (
			isCategoryActive &&
			subCategories.length > 0 &&
			!_.intersection(selectedCategories, subCategories).length
		) {
			return setError(`${namePrefix}.enabled` as any, {
				type: "manual",
			});
		}
		return clearErrors(`${namePrefix}.enabled` as any);
	}, [
		selectedCategories,
		isCategoryActive,
		clearErrors,
		namePrefix,
		setError,
		budgetCategory,
	]);

	const handleCheck = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const categoryId = e.target.getAttribute("data-value");
			if (!categoryId) return;

			if (e.target.checked && !selectedCategories.includes(categoryId)) {
				onChange(selectCategory(categoryId, selectedCategories));
			}
			if (!e.target.checked && selectedCategories.includes(categoryId)) {
				onChange(deselectCategory(categoryId, selectedCategories));
			}
		},
		[selectedCategories, onChange, categories]
	);

	return (
		<div>
			{errors.amounts && errors.amounts[fieldName] && (
				<Alert variant="warning">
					Es muss mindestens eine Kategorie ausgewählt werden.
				</Alert>
			)}
			{categories.map(
				(category) =>
					shouldShowDeprecatedCategory(category) && (
						<Checkbox
							key={category.id}
							label={category.name}
							value={isChecked(category)}
							onChange={handleCheck}
							data-value={category.id}
							data-testid={`category-${category.id}`}
						/>
					)
			)}
		</div>
	);
}

function selectCategory(categoryId: string, selectedCategories: string[]) {
	return Array.from(new Set([...selectedCategories, categoryId]));
}

function deselectCategory(categoryId: string, selectedCategories: string[]) {
	return selectedCategories.filter((id) => id !== categoryId);
}
